import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const companyName = process.env.REACT_APP_COMPANY_NAME || 'autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';

export default function HowItWorks() {
  const [activeBot, setActiveBot] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const botTypes = [
    {
      icon: '💬',
      name: 'WhatsApp Bot',
      tagline: 'Chat with customers where they already are',
      color: '#25d366',
      who: 'Perfect for barbers, salons, restaurants, delivery services, real estate agents — any business where customers already WhatsApp you.',
      what: 'Your customers send a WhatsApp message to your business number. Instead of you typing a reply, the bot handles it automatically. It can answer questions, take bookings, send your menu or price list, and even collect their details.',
      example: [
        { from: 'customer', msg: 'Hi, what are your prices?' },
        { from: 'bot', msg: 'Hey! Here are our prices 👇\n✂️ Men\'s cut — $10\n👦 Kids — $7\n🧔 Beard trim — $5\n\nWant to book an appointment?' },
        { from: 'customer', msg: 'Yes please, tomorrow at 10am' },
        { from: 'bot', msg: '✅ Booked! See you tomorrow at 10am. We\'ll send a reminder the night before.' },
      ],
      setup: [
        { step: 'Option A — Use your own number', desc: 'We register your existing business number with WhatsApp\'s official Business API. Customers always see YOUR number. Takes 1–3 days for Meta to approve — we handle everything.', fee: '$20 one-time setup fee', feeColor: '#f59e0b' },
        { step: 'Option B — Already have API credentials?', desc: 'If you already have a Meta Developer account with a Phone Number ID and Access Token, just paste them in. Goes live instantly.', fee: 'No setup fee', feeColor: '#10b981' },
      ],
    },
    {
      icon: '🌐',
      name: 'Website Chat Widget',
      tagline: 'A chat bubble on your website — no coding',
      color: '#5B4FE8',
      who: 'Great for any business with a website. Especially useful if you get enquiries via your site and struggle to respond fast enough.',
      what: 'We give you a small piece of code — one line — that you paste into your website. A chat bubble appears in the corner. Visitors click it, type a question, and the bot answers. Works on WordPress, Wix, Squarespace, or plain HTML.',
      example: [
        { from: 'customer', msg: 'Do you deliver to Borrowdale?' },
        { from: 'bot', msg: 'Yes! We deliver to all Harare suburbs. Delivery usually takes 45–60 minutes. Want to place an order?' },
        { from: 'customer', msg: 'What\'s the minimum order?' },
        { from: 'bot', msg: 'Minimum order is $15. Here\'s our menu: [link]. You can also call us on 077 123 4567.' },
      ],
      setup: [
        { step: 'Tell us your website URL', desc: 'We create your bot and generate your unique embed code.', fee: 'No extra fee', feeColor: '#10b981' },
        { step: 'Copy one line of code', desc: 'We email you a single <script> tag. Paste it before the </body> tag in your website — most website builders have a "custom code" section.', fee: '', feeColor: '' },
        { step: 'Done — goes live instantly', desc: 'The chat bubble appears on your site within minutes. No developer needed.', fee: '', feeColor: '' },
      ],
    },
    {
      icon: '📘',
      name: 'Facebook Messenger Bot',
      tagline: 'Auto-reply to your Facebook Page messages',
      color: '#1877f2',
      who: 'Ideal if your business has a Facebook Page and you get messages there that you\'re slow to reply to.',
      what: 'When someone messages your Facebook business page, the bot replies automatically. Same idea as WhatsApp — it can answer questions, share information, and collect leads — but on Facebook instead.',
      example: [
        { from: 'customer', msg: 'Are you open on Sundays?' },
        { from: 'bot', msg: 'Yes! We\'re open Sunday 9am–4pm. We\'re at 45 Borrowdale Road, Harare. Anything else I can help with?' },
        { from: 'customer', msg: 'Can I book a table for 4?' },
        { from: 'bot', msg: 'Of course! What date and time works for you?' },
      ],
      setup: [
        { step: 'Option A — We set it up for you', desc: 'Just give us your Facebook Page name or URL. Our team connects everything and goes live within 1–2 business days.', fee: '$50 one-time setup fee', feeColor: '#f59e0b' },
        { step: 'Option B — Do it yourself', desc: 'If you have a Meta Developer account, you can paste your own Page ID and Page Access Token and go live immediately.', fee: 'No extra fee', feeColor: '#10b981' },
      ],
    },
    {
      icon: '🎙️',
      name: 'Voice Bot',
      tagline: 'An AI voice assistant on your website',
      color: '#f59e0b',
      who: 'Good for businesses that want something more premium on their website — or industries where talking feels more natural than typing (e.g. legal, financial, medical).',
      what: 'A microphone button appears on your website. Visitors can speak their question out loud. The AI understands what they said and replies — either in voice or text. It learns from your website content.',
      example: [
        { from: 'customer', msg: '🎙️ "What are your opening hours?"' },
        { from: 'bot', msg: '🔊 "We\'re open Monday to Friday from 8am to 5pm, and Saturday from 9am to 1pm. Is there anything else I can help you with?"' },
      ],
      setup: [
        { step: 'Our team sets it all up', desc: 'Voice bots need a bit more configuration. We build it for you, train it on your website content, and send you an embed code when it\'s ready.', fee: 'Included in Agency plan', feeColor: '#7c3aed' },
        { step: 'Setup takes 2–5 business days', desc: 'We\'ll be in touch by email throughout the process.', fee: '', feeColor: '' },
      ],
    },
  ];

  const steps = [
    {
      num: '01',
      icon: '🏢',
      title: 'Pick your channel',
      desc: 'WhatsApp, website, Facebook, or voice? Most businesses start with WhatsApp because their customers are already there.',
    },
    {
      num: '02',
      icon: '📋',
      title: 'Tell us about your business',
      desc: 'Business name, industry, and a few details. Takes about 2 minutes to fill in. No tech knowledge required.',
    },
    {
      num: '03',
      icon: '✨',
      title: 'Pick an industry template',
      desc: "We've pre-built conversation flows for barbers, restaurants, clothing stores, real estate, and more. Your bot starts with sensible defaults for your type of business.",
    },
    {
      num: '04',
      icon: '🚀',
      title: "You're live",
      desc: 'Website and Facebook bots go live almost immediately. WhatsApp bots take 1–3 days while Meta verifies your number. We handle all of that.',
    },
    {
      num: '05',
      icon: '✏️',
      title: 'Customise whenever you like',
      desc: "Use our visual Flow Builder to edit what the bot says, add new questions and answers, update your prices — no coding. It's like editing a document.",
    },
  ];

  const costs = [
    {
      label: 'Monthly subscription',
      icon: '📅',
      color: '#5B4FE8',
      plain: 'This is what you pay every month to keep your bot running. Think of it like a phone contract — it keeps the service on.',
      tiers: [
        { name: 'Free', price: '$0/mo', note: '1 bot · 100 chats/month' },
        { name: 'Starter', price: '$15/mo', note: '1 bot · 500 chats/month' },
        { name: 'Business', price: '$45/mo', note: '3 bots · 2,000 chats/month' },
        { name: 'Agency', price: '$120/mo', note: '10 bots · 10,000 chats/month' },
      ],
    },
    {
      label: 'One-time setup fee',
      icon: '⚡',
      color: '#f59e0b',
      plain: "This is charged ONCE when you create certain types of bots. It covers the work we do to get your bot registered and connected — like registering your WhatsApp number with Meta. You never pay it again, even if you upgrade your plan.",
      tiers: [
        { name: 'Website bot', price: 'Free', note: 'No setup fee — goes live instantly' },
        { name: 'WhatsApp (we register your number)', price: '$20', note: 'Paid once · number registration with Meta' },
        { name: 'WhatsApp (own API credentials)', price: 'Free', note: 'Paste your Phone Number ID & Access Token' },
        { name: 'Facebook (we set up)', price: '$50', note: 'Paid once · we connect your page' },
        { name: 'Facebook (self setup)', price: 'Free', note: 'You connect it yourself via Meta credentials' },
      ],
    },
  ];

  const payments = [
    { icon: '💳', name: 'Visa / Mastercard', desc: 'Any debit or credit card. Processed securely by Stripe.' },
    { icon: '📱', name: 'EcoCash', desc: 'Pay with your EcoCash number. We send a prompt to your phone.' },
    { icon: '📱', name: 'OneMoney', desc: 'Pay with your NetOne OneMoney number.' },
    { icon: '🅿️', name: 'PayPal', desc: 'Pay from your PayPal account or any card linked to it.' },
  ];

  const faqs = [
    { q: 'Do I need to be technical to use this?', a: "Not at all. If you can fill in a form and send a WhatsApp message, you can set up a bot. Our setup wizard walks you through every step. The only 'technical' thing you might do is paste one line of code into your website — and even that has a 4-step guide with pictures." },
    { q: 'What happens to my existing WhatsApp conversations?', a: "Nothing changes for your current chats. The bot only handles NEW incoming messages. Your existing conversations and contacts stay exactly where they are." },
    { q: 'Can I turn the bot off and reply manually?', a: "Yes. You can pause the bot any time from your dashboard. When it's paused, messages come straight to you as normal. You can also set it up so the bot hands over to you when someone asks to 'speak to a person'." },
    { q: 'What if a customer asks something the bot doesn\'t know?', a: "You choose what happens: the bot can say 'Let me get back to you on that' and collect their number, it can hand the conversation to you directly, or it can say 'I\'m not sure — please call us on [number]'. You decide." },
    { q: 'What does the setup fee actually pay for?', a: "For WhatsApp bots, Meta (the company that owns WhatsApp) requires every business to go through an approval process before they can use the WhatsApp Business API. We handle that entire process for you — submitting your details, verifying your number, and getting everything connected. The $20 fee covers that work." },
    { q: 'Can I change what the bot says after it\'s live?', a: "Yes, any time. Log into your dashboard, open the Flow Builder, and edit the bot\'s replies like you\'d edit a document. Changes go live immediately." },
    { q: 'What happens if I go over my conversation limit?', a: "We email you when you\'re at 80% of your limit. If you hit the cap, the bot pauses until the next billing period. We never charge surprise overage fees. If you\'re regularly hitting the limit, it\'s a sign you should upgrade — and that means your business is doing well!" },
    { q: 'Is there a contract?', a: "No. Month-to-month, cancel any time. No cancellation fees, no notice period. Though once you see how much time it saves, most people don\'t want to cancel." },
  ];

  const active = botTypes[activeBot];

  return (
    <>
      <SEO
        title={`How It Works | ${companyName}`}
        description="Plain-English guide to setting up your WhatsApp, website, or Facebook bot. Covers bot types, setup steps, pricing and payment."
        keywords="how it works, WhatsApp bot setup, chatbot guide, Zimbabwe"
        type="website"
      />

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }

        .hiw-hero { padding: 120px 24px 64px; }
        .hiw-section { padding: 72px 24px; }
        .hiw-inner { max-width: 1040px; margin: 0 auto; }

        .bot-tabs { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 32px; }
        .chat-bubble-customer { background: #e2e8f0; color: #1e293b; border-radius: 18px 18px 4px 18px; padding: 10px 14px; font-size: 14px; max-width: 75%; align-self: flex-end; line-height: 1.5; white-space: pre-wrap; }
        .chat-bubble-bot { background: #5B4FE8; color: white; border-radius: 18px 18px 18px 4px; padding: 10px 14px; font-size: 14px; max-width: 75%; align-self: flex-start; line-height: 1.5; white-space: pre-wrap; }

        .steps-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0; }
        .costs-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        .payments-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }

        .faq-item button:hover { background: rgba(255,255,255,0.03); }

        @media (max-width: 900px) {
          .steps-grid { grid-template-columns: 1fr; gap: 0; }
          .costs-grid { grid-template-columns: 1fr; }
          .payments-grid { grid-template-columns: repeat(2, 1fr); }
          .bot-detail-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .hiw-hero { padding: 96px 20px 48px; }
          .hiw-section { padding: 52px 20px; }
          .payments-grid { grid-template-columns: 1fr; }
          h1 { font-size: 34px !important; }
          h2 { font-size: 24px !important; }
          .bot-tabs { gap: 8px; }
        }
      `}</style>

      <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: '#0a0914', color: 'white', minHeight: '100vh' }}>

        {/* ── NAV ── */}
        <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, padding: '0 32px', height: '60px', background: 'rgba(10,9,20,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/"><img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain' }} /></Link>
          <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
            <Link to="/pricing" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>Pricing</Link>
            <Link to="/about" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>About</Link>
            <Link to="/register" style={{ background: '#5B4FE8', color: 'white', padding: '8px 20px', borderRadius: '20px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>Get started</Link>
          </div>
        </nav>

        {/* ── HERO ── */}
        <div className="hiw-hero" style={{ textAlign: 'center', maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ display: 'inline-block', background: 'rgba(91,79,232,0.18)', border: '1px solid rgba(91,79,232,0.35)', color: '#a5b4fc', fontSize: '11px', fontWeight: '700', padding: '4px 14px', borderRadius: '20px', letterSpacing: '0.08em', marginBottom: '20px' }}>
            PLAIN ENGLISH GUIDE
          </div>
          <h1 style={{ fontSize: '50px', fontWeight: '800', letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: '18px' }}>
            How does this<br />
            <span style={{ color: '#818cf8' }}>actually work?</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '17px', lineHeight: 1.75, marginBottom: '36px' }}>
            No jargon. Just a clear explanation of what a bot is, how to set one up, what it costs, and how you pay — so you can decide if it's right for your business.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="#what-is-a-bot" style={{ background: '#5B4FE8', color: 'white', padding: '12px 24px', borderRadius: '30px', textDecoration: 'none', fontSize: '14px', fontWeight: '700' }}>Start here →</a>
            <a href="#costs" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'white', padding: '12px 24px', borderRadius: '30px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>Jump to pricing</a>
          </div>
        </div>

        {/* ── WHAT IS A BOT ── */}
        <div id="what-is-a-bot" className="hiw-section" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="hiw-inner">
            <div style={{ textAlign: 'center', marginBottom: '52px' }}>
              <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>First things first</div>
              <h2 style={{ fontSize: '34px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '14px' }}>What is a bot, exactly?</h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '16px', maxWidth: '580px', margin: '0 auto', lineHeight: 1.75 }}>
                A bot is a piece of software that chats with your customers automatically — so you don't have to. Think of it as a very fast, very patient employee who works 24/7 and never needs a day off.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '48px' }}>
              {[
                { icon: '😴', title: 'Works while you sleep', desc: 'A customer messages at 11pm asking your prices. Your bot replies in seconds. You wake up to a new lead.' },
                { icon: '🔁', title: 'Handles the same questions, forever', desc: '"What are your hours?" gets asked 50 times a week. The bot answers it 50 times so you don\'t have to.' },
                { icon: '🧠', title: 'Learns your business', desc: 'You tell it your menu, prices, location, and booking process. It uses that information in every conversation.' },
                { icon: '📲', title: 'Talks on apps people already use', desc: 'WhatsApp, your website, Facebook. Customers don\'t download anything new — the bot goes to them.' },
              ].map((item, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '24px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>{item.icon}</div>
                  <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '7px' }}>{item.title}</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: 1.65 }}>{item.desc}</div>
                </div>
              ))}
            </div>

            {/* Real analogy box */}
            <div style={{ background: 'rgba(91,79,232,0.08)', border: '1px solid rgba(91,79,232,0.2)', borderRadius: '16px', padding: '28px 32px', display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
              <div style={{ fontSize: '36px', flexShrink: 0 }}>💡</div>
              <div>
                <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px', color: '#a5b4fc' }}>Think of it like this</div>
                <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '15px', lineHeight: 1.75 }}>
                  You know those big shops that have a receptionist at the door? She greets every customer, answers basic questions, and directs them to the right place. Your bot is that receptionist — but for your WhatsApp, website, or Facebook page. And she never goes for lunch.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── BOT TYPES ── */}
        <div id="bot-types" className="hiw-section" style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="hiw-inner">
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Choose your channel</div>
              <h2 style={{ fontSize: '34px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '12px' }}>The 4 types of bots</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px' }}>Click each one to see how it works, who it's for, and what setup looks like.</p>
            </div>

            {/* Tabs */}
            <div className="bot-tabs">
              {botTypes.map((bot, i) => (
                <button key={i} onClick={() => setActiveBot(i)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '30px', border: activeBot === i ? `2px solid ${bot.color}` : '1px solid rgba(255,255,255,0.1)', background: activeBot === i ? bot.color + '18' : 'rgba(255,255,255,0.04)', color: activeBot === i ? 'white' : 'rgba(255,255,255,0.5)', fontWeight: activeBot === i ? '700' : '500', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>
                  <span>{bot.icon}</span> {bot.name}
                </button>
              ))}
            </div>

            {/* Bot detail */}
            <div className="bot-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>
              {/* Left: info */}
              <div>
                <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '28px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{ fontSize: '32px' }}>{active.icon}</span>
                    <div>
                      <div style={{ fontWeight: '800', fontSize: '18px' }}>{active.name}</div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px' }}>{active.tagline}</div>
                    </div>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: active.color, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>Who is it for?</div>
                    <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: 1.7 }}>{active.who}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: active.color, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>What does it do?</div>
                    <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: 1.7 }}>{active.what}</div>
                  </div>
                </div>

                {/* Setup options */}
                <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: active.color, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '16px' }}>How to set it up</div>
                  {active.setup.map((s, i) => (
                    <div key={i} style={{ marginBottom: i < active.setup.length - 1 ? '16px' : 0, paddingBottom: i < active.setup.length - 1 ? '16px' : 0, borderBottom: i < active.setup.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                      <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>{s.step}</div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', lineHeight: 1.6, marginBottom: s.fee ? '8px' : 0 }}>{s.desc}</div>
                      {s.fee && (
                        <span style={{ display: 'inline-block', background: s.feeColor + '18', color: s.feeColor, border: `1px solid ${s.feeColor}30`, fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px' }}>{s.fee}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: mock conversation */}
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'hidden' }}>
                <div style={{ background: active.color + '18', borderBottom: `1px solid ${active.color}30`, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '18px' }}>{active.icon}</span>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '14px' }}>Example conversation</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>What your customers actually see</div>
                  </div>
                </div>
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '300px' }}>
                  {active.example.map((msg, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: msg.from === 'customer' ? 'flex-end' : 'flex-start' }}>
                      <div className={msg.from === 'customer' ? 'chat-bubble-customer' : 'chat-bubble-bot'} style={{ background: msg.from === 'customer' ? 'rgba(255,255,255,0.1)' : active.color, color: 'white', borderRadius: msg.from === 'customer' ? '18px 18px 4px 18px' : '18px 18px 18px 4px', padding: '10px 14px', fontSize: '13px', maxWidth: '75%', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                        {msg.from === 'bot' && <span style={{ fontSize: '10px', opacity: 0.7, display: 'block', marginBottom: '3px' }}>🤖 Bot</span>}
                        {msg.from === 'customer' && <span style={{ fontSize: '10px', opacity: 0.7, display: 'block', marginBottom: '3px' }}>👤 Customer</span>}
                        {msg.msg}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '12px 18px', borderTop: `1px solid rgba(255,255,255,0.06)`, fontSize: '12px', color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>
                  This all happens automatically — no one types it
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── STEP BY STEP ── */}
        <div id="setup-steps" className="hiw-section">
          <div className="hiw-inner">
            <div style={{ textAlign: 'center', marginBottom: '52px' }}>
              <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>The process</div>
              <h2 style={{ fontSize: '34px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '12px' }}>From sign-up to live bot</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px' }}>Here's exactly what happens after you click "Get started"</p>
            </div>

            <div style={{ position: 'relative' }}>
              {/* Connector line (desktop) */}
              <div style={{ position: 'absolute', top: '28px', left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, #5B4FE8, #7c3aed)', opacity: 0.3, borderRadius: '1px' }} className="steps-line" />

              <div className="steps-grid">
                {steps.map((s, i) => (
                  <div key={i} style={{ textAlign: 'center', padding: '0 12px', position: 'relative' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '22px', boxShadow: '0 0 20px rgba(91,79,232,0.4)', position: 'relative', zIndex: 1 }}>
                      {s.icon}
                    </div>
                    <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '800', letterSpacing: '0.08em', marginBottom: '6px' }}>STEP {s.num}</div>
                    <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '8px', lineHeight: 1.3 }}>{s.title}</div>
                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', lineHeight: 1.65 }}>{s.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '48px', padding: '24px 28px', background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '14px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <div style={{ fontSize: '28px', flexShrink: 0 }}>⏱️</div>
              <div>
                <div style={{ fontWeight: '700', fontSize: '15px', color: '#34d399', marginBottom: '6px' }}>How long does it take?</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: 1.7 }}>
                  <strong style={{ color: 'rgba(255,255,255,0.85)' }}>Website bot:</strong> Live in under 10 minutes. &nbsp;
                  <strong style={{ color: 'rgba(255,255,255,0.85)' }}>Facebook bot (self setup):</strong> Live in 20 minutes. &nbsp;
                  <strong style={{ color: 'rgba(255,255,255,0.85)' }}>WhatsApp bot (own API credentials):</strong> Instant. &nbsp;
                  <strong style={{ color: 'rgba(255,255,255,0.85)' }}>WhatsApp bot (we register your number):</strong> 1–3 business days while Meta verifies. We handle all of it.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── COSTS ── */}
        <div id="costs" className="hiw-section" style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="hiw-inner">
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <div style={{ color: '#f59e0b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>No surprises</div>
              <h2 style={{ fontSize: '34px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '12px' }}>What does it cost?</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', maxWidth: '520px', margin: '0 auto' }}>There are two kinds of charges. Let's explain both clearly.</p>
            </div>

            <div className="costs-grid">
              {costs.map((cost, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${cost.color}25`, borderTop: `3px solid ${cost.color}`, borderRadius: '16px', padding: '28px' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{ fontSize: '28px' }}>{cost.icon}</span>
                    <div style={{ fontWeight: '800', fontSize: '18px' }}>{cost.label}</div>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: 1.75, marginBottom: '24px', padding: '14px', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', borderLeft: `3px solid ${cost.color}` }}>
                    {cost.plain}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {cost.tiers.map((tier, j) => (
                      <div key={j} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'rgba(255,255,255,0.04)', borderRadius: '10px' }}>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>{tier.name}</div>
                          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px', marginTop: '2px' }}>{tier.note}</div>
                        </div>
                        <div style={{ fontWeight: '800', fontSize: '16px', color: cost.color }}>{tier.price}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Example total cost box */}
            <div style={{ marginTop: '28px', background: 'rgba(91,79,232,0.07)', border: '1px solid rgba(91,79,232,0.2)', borderRadius: '16px', padding: '28px' }}>
              <div style={{ fontWeight: '800', fontSize: '17px', marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span>🧮</span> Real example: A barber shop on WhatsApp
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                {[
                  { label: 'Month 1', desc: 'Setup fee + first month', amount: '$35', note: '$20 setup + $15 Starter plan', color: '#f59e0b' },
                  { label: 'Month 2 onwards', desc: 'Just the monthly plan', amount: '$15/mo', note: 'Setup fee is never charged again', color: '#10b981' },
                  { label: 'Per conversation', desc: 'Your plan includes 500/month', amount: '$0', note: 'No per-message charges', color: '#5B4FE8' },
                ].map((item, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '18px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: item.color, letterSpacing: '0.06em', marginBottom: '4px' }}>{item.label}</div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: 'white', marginBottom: '4px' }}>{item.amount}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>{item.note}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── PAYMENT ── */}
        <div id="payment" className="hiw-section">
          <div className="hiw-inner">
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Paying is easy</div>
              <h2 style={{ fontSize: '34px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '12px' }}>How do I pay?</h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', maxWidth: '480px', margin: '0 auto' }}>We accept all the payment methods used in Zimbabwe — including EcoCash.</p>
            </div>

            <div className="payments-grid">
              {payments.map((p, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', padding: '24px', textAlign: 'center' }}>
                  <div style={{ fontSize: '36px', marginBottom: '12px' }}>{p.icon}</div>
                  <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '6px' }}>{p.name}</div>
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', lineHeight: 1.55 }}>{p.desc}</div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
              {[
                { icon: '🔒', title: 'Secure payments', desc: 'Card payments are processed by Stripe — the same system used by Amazon and Shopify. We never see or store your card number.' },
                { icon: '📱', title: 'EcoCash is simple', desc: 'Enter your EcoCash number, we send a payment request to your phone. You approve it. Done.' },
                { icon: '📄', title: 'You get an invoice', desc: 'Every payment generates a downloadable PDF invoice for your records.' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '18px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px' }}>
                  <span style={{ fontSize: '22px', flexShrink: 0 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>{item.title}</div>
                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', lineHeight: 1.6 }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── FAQ ── */}
        <div className="hiw-section" style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="hiw-inner" style={{ maxWidth: '720px' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h2 style={{ fontSize: '32px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '10px' }}>Still have questions?</h2>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '15px' }}>The things people ask us most before signing up.</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {faqs.map((faq, i) => (
                <div key={i} className="faq-item" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    style={{ width: '100%', padding: '17px 20px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left', fontFamily: 'inherit', gap: '12px', transition: 'background 0.15s' }}
                  >
                    <span style={{ color: 'white', fontWeight: '600', fontSize: '15px', lineHeight: 1.4, flex: 1 }}>{faq.q}</span>
                    <span style={{ color: '#5B4FE8', fontSize: '20px', flexShrink: 0, display: 'inline-block', transition: 'transform 0.2s', transform: openFaq === i ? 'rotate(45deg)' : 'none' }}>+</span>
                  </button>
                  {openFaq === i && (
                    <div style={{ padding: '0 20px 18px', color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: 1.75 }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="hiw-section" style={{ background: 'linear-gradient(135deg, #1a1040 0%, #0c0820 100%)', textAlign: 'center' }}>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <div style={{ fontSize: '44px', marginBottom: '14px' }}>🚀</div>
            <h2 style={{ fontSize: '32px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '12px' }}>Ready to give it a go?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', lineHeight: 1.7, marginBottom: '28px' }}>
              Start on the free plan — no credit card, no commitment. If you get stuck, our team is on WhatsApp.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/register" style={{ background: '#5B4FE8', color: 'white', padding: '14px 32px', borderRadius: '30px', textDecoration: 'none', fontSize: '15px', fontWeight: '700', boxShadow: '0 0 40px rgba(91,79,232,0.35)' }}>
                Get started free →
              </Link>
              <a href={`mailto:${companyEmail}`} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'white', padding: '14px 32px', borderRadius: '30px', textDecoration: 'none', fontSize: '15px', fontWeight: '600' }}>
                Ask us a question
              </a>
            </div>
            <div style={{ marginTop: '14px', fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>
              Free plan · No card required · Cancel any time
            </div>
          </div>
        </div>

      </div>
    </>
  );
}